<template>
  <sp-menu
    class="date-filter"
    :open="menuOpen"
    :close-on-content-click="closeOnContentClick"
    @update-open="handleUpdateOpen"
  >
    <div slot="activator">
      <slot name="activator" :label="label" :text="activatorTitle" :prepend-icon="prependIcon" :open="open">
        <DefaultFilterActivator
          :value="activatorTitle"
          :label="label"
          :prepend-icon="prependIcon"
          no-divider
          class="date-filter__activator"
          @clear="clearSelectedModel"
        />
      </slot>
    </div>

    <sp-card>
      <sp-card-content>
        <sp-label block>
          {{ cardLabel }}
        </sp-label>
        <sp-date-picker-two
          ref="datePicker"
          v-bind="attrs"
          v-model="model"
          :input-test-id="TestId.finder.dateFilterInput"
          :day-test-id="TestId.finder.dateFilterDay"
          :open="open"
          :name="name"
          :range="range"
          :inline="inline"
          :display-format="displayFormat"
          @update-open="handleUpdateOpen"
        ></sp-date-picker-two>
      </sp-card-content>
    </sp-card>
  </sp-menu>
</template>

<script setup>
import { watchImmediate } from "@vueuse/core";
import { computed, ref, useAttrs, useTemplateRef, watch } from "vue";
import { useForm } from "#ce/composables/useForm";
import { booleanAttribute, toBoolean, trimToUndefined } from "#ce/utils/props";
import { TestId } from "#/test-ids";
import DefaultFilterActivator from "#ce/apps/shared/filters/components/DefaultFilterActivator.ce.vue";

const emit = defineEmits(["update:open", "change", "update:model-value"]);

const attrs = useAttrs();

const { mirrorFormValue } = useForm();

const props = defineProps({
  modelValue: {
    type: String,
    default: undefined,
  },
  name: {
    type: String,
    default: "date",
  },
  label: {
    type: String,
    default: "Date",
  },
  cardLabel: {
    type: String,
    default: undefined,
  },
  displayFormat: {
    type: String,
    default: "F j, Y",
  },
  prependIcon: {
    type: String,
    default: "event-filled",
  },
  hidePointer: {
    type: Boolean,
    default: false,
  },
  open: {
    type: Boolean,
    default: false,
  },
  inline: {
    type: Boolean,
    default: false,
  },
  range: booleanAttribute(),
  permanent: booleanAttribute(),
  width: {
    type: String,
    default: undefined,
  },
  closeOnSelect: booleanAttribute(),
  closeOnContentClick: booleanAttribute(),
});

defineExpose({ props });

const menuOpen = ref(toBoolean(props.open));
const range = computed(() => toBoolean(props.range));

watch(
  () => props.open,
  (value) => (menuOpen.value = toBoolean(value)),
);
watch(menuOpen, (value) => {
  emit("update:open", value);
});

const model = ref(props.modelValue);

watch(
  () => props.modelValue,
  (value) => (model.value = value),
);

// A modelValue prop sync mirrors silently; only the filter's own updates report upward.
function emitModelValue(value) {
  if (value === props.modelValue) {
    return;
  }
  emit("update:model-value", value);
}

watchImmediate(model, emitModelValue);

// The host app dispatches the one change per user update.
mirrorFormValue(model);

const datePicker = useTemplateRef("datePicker");
const displayValue = ref();

watchImmediate(
  () => [datePicker.value?.displayText, model.value],
  ([value]) => (displayValue.value = value),
);

const activatorTitle = computed(() => trimToUndefined(displayValue.value));

function clearSelectedModel() {
  model.value = undefined;
}

function handleUpdateOpen({ detail }) {
  const [isOpen] = detail;
  menuOpen.value = isOpen;
}
</script>

<style scoped>
.date-filter {
  --sp-comp-popup-width: max-content !important;
}

sp-label {
  margin-top: var(--sp-ref-spacing-6);
  margin-bottom: var(--sp-ref-spacing-4);
}
sp-card-content {
  padding-inline: var(--sp-comp-date-filter-padding-inline, var(--sp-ref-spacing-6));
}
</style>
